import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { VoiceType, TaskAttachment } from '../types/api';
import VoiceSelector from '../components/VoiceSelector';
import ScriptStyleSelector from '../components/ScriptStyleSelector';
import { useToast } from '../hooks/useToast';
import { validateFileType, validateFileSize, formatFileSize } from '../utils/format';

export default function SubmitPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [voices, setVoices] = useState<VoiceType[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<VoiceType | null>(null);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [taskText, setTaskText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.getVoiceTypes().then((res) => setVoices(res.voice_types || [])).catch(() => showToast('加载音色失败', 'error'));
  }, []);

  const handleFiles = async (files: FileList) => {
    const validFiles = Array.from(files).filter((file) => {
      if (!validateFileType(file.name)) {
        showToast(`不支持的文件格式: ${file.name.split('.').pop()}`, 'error');
        return false;
      }
      if (!validateFileSize(file)) {
        showToast(`文件 ${file.name} 超过大小限制`, 'error');
        return false;
      }
      return true;
    });
    if (!validFiles.length) {
      showToast('没有有效的文件', 'warning');
      return;
    }
    for (const file of validFiles) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await api.uploadTaskAttachment(file, file.name, (progress) => {
        setUploadProgress(progress);
      });
      if (response?.id) {
        setAttachments((prev) => [...prev, { ...response, original_filename: response.original_filename || file.name }]);
        showToast(`${file.name} 上传成功`, 'success');
      } else {
        throw new Error('上传响应无效');
      }
    } catch (error) {
      showToast(`${file.name} 上传失败: ${error instanceof Error ? error.message : ''}`, 'error');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const canSubmit = (taskText.trim().length > 0 || attachments.length > 0) && selectedVoice !== null;

  const handleSubmit = async () => {
    if (!taskText.trim() && attachments.length === 0) {
      showToast('请上传文档或输入文本', 'warning');
      return;
    }
    if (!selectedVoice) {
      showToast('请选择音色', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const text = taskText.trim();
      const style = selectedStyle || '科普口播稿';
      const attachmentIds = attachments.map((a) => a.id);

      let successCount = 0;
      let failCount = 0;

      if (attachmentIds.length > 0) {
        for (let i = 0; i < attachmentIds.length; i++) {
          const attachmentId = attachmentIds[i]!;
          const attachment = attachments[i]!;
          const taskTitle = generateTaskTitle(attachment, i);
          try {
            await api.createTaskSubmission({
              title: taskTitle,
              text,
              style,
              voice_type: selectedVoice.voice_type,
              attachment_ids: [attachmentId],
            });
            successCount++;
          } catch {
            failCount++;
          }
        }
      } else {
        await api.createTaskSubmission({
          title: '文本任务',
          text,
          style,
          voice_type: selectedVoice.voice_type,
          attachment_ids: [],
        });
        successCount = 1;
      }

      if (successCount > 0) {
        const message = failCount > 0
          ? `提交完成：成功 ${successCount} 个，失败 ${failCount} 个`
          : `成功提交 ${successCount} 个任务`;
        showToast(message, 'success');
        setTimeout(() => navigate('/tasks'), 1500);
      } else {
        showToast('所有任务提交失败', 'error');
      }
    } catch {
      showToast('提交任务失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const generateTaskTitle = (attachment: TaskAttachment, index: number): string => {
    const filename = attachment?.original_filename || `附件${index + 1}`;
    const baseName = filename.replace(/\.[^.]+$/, '');
    return `${baseName}-任务${index + 1}`;
  };

  const handleReset = () => {
    setAttachments([]);
    setSelectedVoice(null);
    setTaskText('');
    setSelectedStyle('');
  };

  return (
    <div className="page-container submit-page">
      <div className="glass-card hero-card d-flex justify-content-between align-items-center">
        <div>
          <div className="hero-eyebrow text-fg-muted mb-1">提交任务</div>
          <h2 className="mb-2">上传文档 · 后台运行</h2>
          <p className="text-fg-muted mb-0">上传文献提交后台任务产出口播稿和音频，支持批量处理</p>
        </div>
      </div>

      <div className="section-card">
        <div className="section-title">文档附件</div>
        <div className="attachment-panel">
          <div className="attachment-header">
            <div className="text-fg-muted">支持多选：PDF / DOCX / TXT（最多10个文件）</div>
            <button className="btn btn-outline-light btn-sm" type="button" onClick={() => fileInputRef.current?.click()}>
              选择文件
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              hidden
              onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          <div
            className={`upload-area mt-3 ${dragOver ? 'dragover' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
            }}
          >
            <i className="fas fa-cloud-upload-alt fa-2x text-primary mb-2"></i>
            <p className="mb-1">点击或拖拽文件到此处上传</p>
            <p className="text-muted small mb-0">单个文件不超过 10MB</p>
          </div>

          {uploading && (
            <div className="upload-progress mt-3">
              <div className="progress">
                <div className="progress-bar" role="progressbar" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-muted small mt-2">上传中...</p>
            </div>
          )}

          {attachments.length === 0 ? (
            <div className="text-fg-muted mt-3">上传文献后，系统会自动抽取文本内容。</div>
          ) : (
            <div className="attachment-list mt-3">
              {attachments.map((att, index) => (
                <div className="attachment-item" key={att.id}>
                  <div>
                    <div className="attachment-name">{att.original_filename}</div>
                    <div className="attachment-meta">约 {formatFileSize(att.file_size || 0)}</div>
                  </div>
                  <button className="link-btn" type="button" onClick={() => removeAttachment(index)}>移除</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="section-card">
        <div className="section-title">口播稿类型</div>
        <p className="text-fg-muted mb-3">系统会根据所选风格自动调整语气、结构与表达方式</p>
        <ScriptStyleSelector value={selectedStyle} onChange={setSelectedStyle} />
      </div>

      <div className="section-card">
        <div className="section-title">配音音色</div>
        <p className="text-fg-muted mb-3">请选择符合场景的说话人音色</p>
        <VoiceSelector
          voices={voices}
          selectedVoice={selectedVoice}
          onChange={setSelectedVoice}
        />
      </div>

      <div className="section-card">
        <div className="section-title">补充文本（可选）</div>
        <textarea
          className="form-control"
          rows={6}
          placeholder="可直接粘贴文献片段或提示。若为空，将只依赖附件内容。"
          maxLength={10000}
          value={taskText}
          onChange={(e) => setTaskText(e.target.value)}
        />
        <div className="form-text">{taskText.length} / 10000 字</div>
      </div>

      <div className="section-card">
        <div className="alert alert-info mb-3">
          <i className="fas fa-info-circle me-2"></i>提交后将在后台处理，可前往查看任务页面追踪状态
        </div>
        <div className="d-grid gap-3">
          <button className="btn btn-success btn-lg" disabled={!canSubmit || submitting} onClick={handleSubmit}>
            {submitting ? (
              <><i className="fas fa-spinner fa-spin me-2"></i>提交中...</>
            ) : (
              <><i className="fas fa-paper-plane me-2"></i>提交任务</>
            )}
          </button>
          <button className="btn btn-outline-secondary" onClick={handleReset}>
            <i className="fas fa-redo me-2"></i>重置
          </button>
        </div>
      </div>
    </div>
  );
}